import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError, handleApiError, handleApiErrorWithSchema } from "../errors";
import schemaValidators from "./schema-validators";
import { AssessmentInfo } from "../types";

interface UserInfo {
	id: number;
	uuid: string;
	userInfo: {
		email: string;
		full_name: string;
		given_name: string;
		family_name: string;
		profile_picture: string;
	};
	institute: {
		id: number;
		name: string;
		lms: string;
	};
}

type Language = "en" | "fr";

type VerityNotification = {
	type: "nps-survey";
	details: {
		url: string;
	};
};

export default class UserClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	getInstituteStudents = async () => {
		try {
			const response = await super.fetch(new URL("/api/verity/user/student-roster", this.config.verityOrigin), {
				credentials: "include",
			});
			const obj: unknown = await response.json();
			if (this.schemaValidators.students.validate(obj)) {
				return obj;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getCourseStudents = async (assessmentInfo: Pick<AssessmentInfo, "courseId">) => {
		if (assessmentInfo.courseId) {
			try {
				const response = await super.fetch(
					new URL(`/api/verity/user/student-roster/${assessmentInfo.courseId}`, this.config.verityOrigin),
					{
						credentials: "include",
					},
				);
				if (response.ok) {
					const obj: unknown = await response.json();
					if (this.schemaValidators.students.validate(obj)) {
						return obj;
					}
				}
			} catch (error) {
				if (error instanceof Error) {
					throw new ApiClientError(error);
				}
			}
		} else {
			console.error("No courseId provided");
		}
		throw new ApiClientError();
	};

	getProctoringExemptionStatus = async (assessmentInfo: Pick<AssessmentInfo, "courseId" | "quizId">) => {
		try {
			const response = await super.fetch(
				new URL(
					`/api/verity/proctoring-exemption/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}/user`,
					this.config.verityOrigin,
				),
				{ credentials: "include" },
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.exemptionStatus.validate(obj)) {
					return obj;
				}
			}
			throw new Error("ExemptionStatus was not type {exempt: boolean}");
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError(new Error(`Unable to fetch exemption status`));
	};

	getProctoringExemptionStudents = async (assessmentInfo: Pick<AssessmentInfo, "courseId" | "quizId">) => {
		if (assessmentInfo.courseId) {
			try {
				const response = await super.fetch(
					new URL(
						`/api/verity/proctoring-exemption/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
						this.config.verityOrigin,
					),
					{ credentials: "include" },
				);
				if (response.ok) {
					const obj: unknown = await response.json();
					if (this.schemaValidators.proctoringExemptions.validate(obj)) {
						return obj;
					}
				} else if (response.status === 404) {
					return [];
				}
			} catch (error) {
				if (error instanceof Error) {
					throw new ApiClientError(error);
				}
			}
		} else {
			console.error("No courseId provided");
		}
		throw new ApiClientError();
	};

	saveProctoringExemptionStudents = async ({
		assessmentInfo,
		students,
	}: {
		assessmentInfo: Pick<AssessmentInfo, "courseId" | "quizId">;
		students: Array<{
			userId: number;
			accommodationNotes?: string | undefined;
			proctoringOptionProfileId?: number | undefined;
		}>;
	}) => {
		try {
			const response = await super.fetch(
				new URL(
					`/api/verity/proctoring-exemption/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
					this.config.verityOrigin,
				),
				{
					method: "POST",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						exemptedStudents: students,
					}),
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getExemptionInfoSuccessStatus = async (assessmentInfo: AssessmentInfo) => {
		if (assessmentInfo.courseId) {
			try {
				const response = await super.fetch(
					new URL(
						`/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
						this.config.verityOrigin,
					),
					{
						method: "POST",
						credentials: "include",
						headers: {
							"Content-Type": "application/json",
						},
					},
				);
				return response.ok;
			} catch (error) {
				return false;
			}
		} else {
			return false;
		}
	};

	createStandaloneUser = ({
		instituteUuid,
		courseUuid,
		assessmentUuid,
		email,
		firstName,
		lastName,
		expiryDate,
		emailTemplateData,
	}: {
		instituteUuid: string;
		courseUuid: string;
		assessmentUuid: string;
		email: string;
		firstName: string;
		lastName: string;
		expiryDate: Date;
		emailTemplateData: Record<string, unknown>;
	}) => {
		return handleApiErrorWithSchema(
			super.fetch(new URL("/api/access-control/single-use-account", this.config.verityOrigin), {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					instituteId: instituteUuid,
					courseId: courseUuid,
					resourceId: assessmentUuid,
					email,
					firstName,
					lastName,
					expiryDate,
					emailTemplateData,
				}),
			}),
			this.schemaValidators.standaloneAccount,
		);
	};

	getUserId = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/user/id`, this.config.verityOrigin), {
				credentials: "include",
			});
			const userId: unknown = await response.json();
			if (this.schemaValidators.studentId.validate(userId)) {
				return userId.id;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getUserInfo = async (userId?: number) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-user${userId != null ? `/${userId}` : ""}`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			const userInfo = (await response.json()) as UserInfo;
			return userInfo;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAccessControlUserInfo = async (userId: string) => {
		try {
			const response = await super.fetch(new URL(`/api/access-control/user/${userId}`, this.config.verityOrigin));
			const userInfo: unknown = await response.json();
			if (this.schemaValidators.accessControlUserInfo.validate(userInfo)) {
				return userInfo;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getUserLanguage = async (userId: number): Promise<Language | null> => {
		try {
			const url = new URL(`/api/verity/user/${userId}/language`, this.config.verityOrigin);
			const response = await super.fetch(url, {
				credentials: "include",
			});
			const language: unknown = await response.json();
			if (this.schemaValidators.language.validate(language)) {
				return language;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	saveUserLanguage = async (userId: number, language: string) => {
		try {
			const url = new URL(`/api/verity/user/${userId}/language`, this.config.verityOrigin);

			const response = await super.fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				body: JSON.stringify({
					language: language,
				}),
			});

			return response.status;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	/**
	 * Get the best available launch URL it is one of:
	 * - This sessions institute origin
	 * - The last course's institute origin
	 * - The last course's URL
	 * - The external tool URL in the last course
	 */
	getLaunchUrl = async () => {
		try {
			const url = new URL(`/api/verity/user/launch`, this.config.verityOrigin);
			const response = await super.fetch(url, {
				credentials: "include",
			});

			if (response.ok) {
				const json = (await response.json()) as { url: string };
				return json;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getNotifications = async ({ instituteUuid }: { instituteUuid?: string }) => {
		const searchParams = new URLSearchParams();
		if (instituteUuid) {
			searchParams.set("instituteId", instituteUuid);
		}
		return (await handleApiErrorWithSchema(
			super.fetch(
				"/api/verity/user/notification" + (searchParams.size > 0 ? `?${searchParams.toString()}` : undefined),
				{
					credentials: "include",
				},
			),
			this.schemaValidators.notifications,
		)) as VerityNotification[];
	};

	dismissNotifications = ({ type }: { type?: VerityNotification["type"] }) => {
		const searchParams = new URLSearchParams();
		if (type) {
			searchParams.set("type", type);
		}
		return handleApiError(
			super.fetch(`/api/verity/user/notification?${searchParams.toString()}`, {
				method: "DELETE",
				credentials: "include",
			}),
		);
	};
}
